import PageLayout from '../../layouts/PageLayout';
import { Section, SectionHeader } from '../../components/shared/Section';
import { HeroRibbon } from '../Home/components/HeroSection';
import { usePageTitle } from '../../hooks/usePageTitle';
import { ExternalLink, FileText, Mail } from 'lucide-react';

export default function About() {
  usePageTitle('About');

  return (
    <PageLayout>
      <HeroRibbon
        title="About"
        subtitle="Research discipline, production systems, and platform architecture applied to problems that have to work at enterprise scale."
      />

      <Section background="light" spacing="sm" withDivider={true}>
        <SectionHeader title="From Research Tools to Production Platforms" />
        <div className="max-w-4xl space-y-5 text-lg leading-relaxed text-slate-600">
          <p>
            Chintan's visualization research at Indiana University from 2009 to
            2011 established a foundation in semantic data, software frameworks,
            and tools built for other researchers. At Ooyala, that foundation
            moved into distributed event processing and search systems. At
            SnagFilms, he applied it to subscription, payment, billing, and
            multi-platform product architecture. At Salesforce, the scope
            expanded into global CDN infrastructure and AI platforms.
          </p>
          <p>
            Each stage supplied tools the next one needed: data models and
            extensible frameworks from research, event-driven systems from media
            infrastructure, end-to-end product ownership from subscription
            platforms, and enterprise-scale delivery from Salesforce.
          </p>
        </div>
      </Section>

      <Section background="white" spacing="sm" withDivider={true}>
        <SectionHeader title="How He Approaches Problems" />
        <div className="max-w-4xl space-y-5 text-lg leading-relaxed text-slate-600">
          <p>
            He starts ambiguous platform work by defining the structure: what
            enters the system, what must come out, and which boundaries need a
            stable contract. Inputs and outputs are the default mental model.
          </p>
          <p>
            Metadata Intelligence follows that model. Salesforce metadata
            artifacts enter a common enrichment pipeline; discovery and quality
            analysis come out through a multi-LLM system with LLM-as-a-Judge
            evaluation and asynchronous fault handling. The structure supports
            standard and custom components without requiring a separate
            processing model for every metadata type.
          </p>
        </div>
      </Section>

      <Section background="light" spacing="sm" withDivider={true}>
        <SectionHeader title="Research and Production Work" />
        <div className="max-w-4xl space-y-5 text-lg leading-relaxed text-slate-600">
          <p>
            Chintan publishes through IEEE and Springer conferences and
            contributes as a committee reviewer. He also owns production systems
            day to day. The research tests ideas formally; the production work
            tests whether those ideas can operate within enterprise constraints.
          </p>
        </div>
      </Section>

      <Section background="white" spacing="sm" withDivider={true}>
        <SectionHeader title="Current Focus" />
        <div className="max-w-4xl space-y-5 text-lg leading-relaxed text-slate-600">
          <p>
            His current work centers on edge computing, AI platform architecture,
            Metadata Intelligence, and agentic systems. He is also part of the
            team that built Quick Pages, an open-beta Salesforce page-building
            experience based on a direct-authored, no-agent LLM approach. The
            work reflects a specific goal: build internal tools with a path into
            Salesforce products.
          </p>
        </div>
      </Section>

      <Section background="light" spacing="sm">
        <SectionHeader title="Collaboration" />
        <div className="max-w-4xl text-lg leading-relaxed text-slate-600">
          <p>
            He is interested in research collaboration, conference and journal
            reviewing, and practitioner writing that connects published work to
            production engineering.
          </p>

          <aside className="mt-8 border-t border-slate-200 pt-5 pb-4" aria-labelledby="also-building-title">
            <h2 id="also-building-title" className="text-lg font-semibold text-slate-500">
              Also Building
            </h2>
            <div className="mt-3">
              <div className="flex flex-col gap-4 py-3 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
                    <Mail className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <p className="min-w-0 text-sm leading-relaxed text-slate-600">
                    <span className="font-semibold text-slate-900">Gmail classifier</span>
                    <span className="text-slate-500"> {' — '}5,000+ emails to 205 in a weekend</span>
                  </p>
                </div>
                <a
                  href="https://github.com/tankchintan/gmail-classifier"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-fit flex-shrink-0 items-center whitespace-nowrap rounded-lg bg-violet-50 px-4 py-2 text-sm font-medium text-violet-600 transition-all duration-300 hover:bg-violet-100 hover:shadow-lg"
                >
                  View repo
                  <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </div>

              <div className="flex flex-col gap-4 border-t border-slate-200 py-3 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
                    <FileText className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <p className="min-w-0 text-sm leading-relaxed text-slate-600">
                    <span className="font-semibold text-slate-900">Recent writing</span>
                    <span className="text-slate-500"> {' — '}Notes from the field</span>
                  </p>
                </div>
                <a
                  href="https://www.linkedin.com/in/chintantank/recent-activity/all/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-fit flex-shrink-0 items-center whitespace-nowrap rounded-lg bg-violet-50 px-4 py-2 text-sm font-medium text-violet-600 transition-all duration-300 hover:bg-violet-100 hover:shadow-lg"
                >
                  Recent writing
                  <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </div>
            </div>
          </aside>
        </div>
      </Section>
    </PageLayout>
  );
}
